import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import styles from './DocumentPanel.module.css';

// Only these link targets survive in the preview. A Word file's hyperlinks come
// from the file itself, and a sandbox-written file could carry a script link.
const SAFE_HREF = /^(https?:|mailto:|#)/i;

const neutralizeLinks = (root) => {
  root.querySelectorAll('a[href]').forEach((a) => {
    if (!SAFE_HREF.test(a.getAttribute('href') || '')) {
      a.removeAttribute('href');
    } else if (!a.getAttribute('href').startsWith('#')) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  });
};

/** Read-only, paged preview of a Word document (docx-preview). */
export default function DocxPreview({ buffer }) {
  const containerRef = useRef(null);
  const [state, setState] = useState('loading'); // loading | ready | error

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !buffer) return undefined;
    let cancelled = false;
    setState('loading');
    container.innerHTML = '';
    // A copy, so the cached bytes stay intact for the download.
    renderAsync(buffer.slice(0), container, undefined, {
      inWrapper: true,
      breakPages: true,
      ignoreLastRenderedPageBreak: true,
      useBase64URL: true,
      renderHeaders: true,
      renderFooters: true,
      experimental: false,
    })
      .then(() => {
        if (cancelled) return;
        neutralizeLinks(container);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [buffer]);

  return (
    <div className={styles.docxPreview}>
      {state === 'loading' && <p className={styles.mutedText}>Preparing the preview…</p>}
      {state === 'error' && (
        <p className={styles.errorText} role="alert">
          This document could not be previewed. Download it to open it in Word.
        </p>
      )}
      <div ref={containerRef} className={styles.docxPages} hidden={state === 'error'} />
    </div>
  );
}
